import { Controller, Form } from 'react-hook-form';
import { isNil } from 'lodash';
import { Box, Button, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { t } from 'i18next';

import { theme } from '@store';
import { CustomModal, SaveChangesModal } from '@components';

import { useCreateTeam } from './hooks';
import { USStateSelect } from 'components/USStateSelect';
import { Select } from 'ui';

export const CreateTeam = ({
  handleOpenTeamDetails,
}: {
  handleOpenTeamDetails: (id: string) => void;
}) => {
  const {
    control,
    handleClose,
    handleOnClick,
    isOpen,
    isSubmitting,
    onSubmit,
    watch,
    teamsShort,
    recordTypes,
    recordsShort,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useCreateTeam(handleOpenTeamDetails);

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        sx={{ fontWeight: 'regular', fontSize: '1rem', color: 'white' }}
        color='secondary'
        variant='contained'
        onClick={handleOnClick}
      >
        {t('adminPanel.teams.addModal.addTeam')}
      </Button>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('adminPanel.teams.addModal.title')}
        isLoading={isSubmitting}
      >
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Controller
            name='name'
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.teams.addModal.name')}
                  error={!isNil(error)}
                  required
                  options={teamsShort}
                  helperText={error?.message}
                />
              );
            }}
          />
          <Controller
            name='description'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type='text'
                size='medium'
                label={t('adminPanel.teams.addModal.description')}
                variant='outlined'
                error={!isNil(error)}
                helperText={error ? error.message : null}
                fullWidth
              />
            )}
          />
          <Typography variant='subtitle2' sx={{ mb: 1, mt: 1 }}>
            {t('adminPanel.teams.drawer.generalTab.managing_record')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'top',
              width: '100%',
              gap: 1,
              mb: 1,
            }}
          >
            <Controller
              name='record_type'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.teams.addModal.record_type')}
                  error={!isNil(error)}
                  required
                  disabled={!watch('name')}
                  options={recordTypes}
                  helperText={error?.message}
                />
              )}
            />
            <Controller
              name='record_id'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.teams.addModal.record_name')}
                  error={!isNil(error)}
                  disabled={!watch('name')}
                  options={recordsShort}
                  helperText={error?.message}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
            }}
          >
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              {t('adminPanel.teams.addModal.contactInformation')}
            </Typography>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='city'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    size='medium'
                    label={t('adminPanel.teams.addModal.city')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    fullWidth
                  />
                )}
              />
              <Controller
                name='state'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <USStateSelect
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : undefined}
                  />
                )}
              />
            </Box>
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              position: 'sticky',
              bottom: 0,
              paddingY: 3,
              backgroundColor: theme.palette.common.white,
              zIndex: 3,
            }}
          >
            <Button
              variant='text'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
              }}
              disabled={isSubmitting}
              onClick={handleClose}
            >
              {t('general.cancel')}
            </Button>
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              sx={{
                fontSize: '0,875rem',
                fontWeight: 'medium',
                lineHeight: '24px',
                color: 'white',
              }}
              disabled={isSubmitting}
            >
              {t('general.ADD')}
            </Button>
          </Box>
        </Form>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </>
  );
};
